import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaTools, FaSpinner, FaCheckCircle, FaBan, FaPlayCircle,
    FaStopCircle, FaHourglassHalf, FaSearch, FaFilter,
    FaCalendarAlt, FaUser, FaChevronRight, FaLayerGroup,
    FaClipboardList, FaRegCheckCircle, FaExclamationCircle
} from 'react-icons/fa';
import './GmudDashboard.css';

const STATUS_CONFIG = {
    Pendente:      { color: 'var(--gmud-status-pending)',    bg: 'var(--gmud-status-pending-bg)',    icon: FaHourglassHalf, label: 'Pendente' },
    Aprovada:      { color: 'var(--gmud-status-approved)',   bg: 'var(--gmud-status-approved-bg)',   icon: FaCheckCircle,   label: 'Aprovada' },
    'Em Execução': { color: 'var(--gmud-status-running)',    bg: 'var(--gmud-status-running-bg)',    icon: FaPlayCircle,    label: 'Em Execução' },
    Concluída:     { color: 'var(--gmud-status-done)',       bg: 'var(--gmud-status-done-bg)',       icon: FaCheckCircle,   label: 'Concluída' },
    Rejeitada:     { color: 'var(--gmud-status-rejected)',   bg: 'var(--gmud-status-rejected-bg)',   icon: FaBan,           label: 'Rejeitada' },
    Cancelada:     { color: 'var(--gmud-status-cancelled)',  bg: 'var(--gmud-status-cancelled-bg)',  icon: FaStopCircle,    label: 'Cancelada' },
};

const TIPO_CONFIG = {
    Padrão:      { color: '#3b82f6', bg: '#eff6ff' },
    Normal:      { color: '#6366f1', bg: '#eef2ff' },
    Emergencial: { color: '#ef4444', bg: '#fef2f2' },
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Pendente;
    const Icon = cfg.icon;
    return (
        <span className="gmud-badge gmud-badge-status" style={{ color: cfg.color, background: cfg.bg }}>
            <Icon size={12} className="gmud-badge-icon" />
            {cfg.label}
        </span>
    );
};

const TipoBadge = ({ tipo }) => {
    const cfg = TIPO_CONFIG[tipo] || { color: '#6b7280', bg: '#f3f4f6' };
    return (
        <span className="gmud-badge gmud-badge-tipo" style={{ color: cfg.color, background: cfg.bg }}>
            {tipo}
        </span>
    );
};

const KpiCard = ({ label, value, sub, color, icon: Icon }) => (
    <div className="gmud-kpi-card">
        <div className="gmud-kpi-header">
            <span className="gmud-kpi-label">{label}</span>
            {Icon && <Icon size={16} style={{ color: color, opacity: 0.8 }} />}
        </div>
        <div className="gmud-kpi-body">
            <span className="gmud-kpi-value" style={{ color }}>{value}</span>
            {sub && <span className="gmud-kpi-sub">{sub}</span>}
        </div>
    </div>
);

const SkeletonRow = () => (
    <tr className="gmud-table-row gmud-skeleton-row">
        {Array.from({ length: 8 }).map((_, i) => (
            <td key={i} className="gmud-table-cell">
                <div className="gmud-skeleton-box" style={{ width: `${50 + Math.random() * 40}%` }} />
            </td>
        ))}
    </tr>
);

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const GmudDashboard = () => {
    const [gmuds, setGmuds]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterTipo, setFilterTipo]     = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get('/api/gmud');
                setGmuds(data);
            } catch {
                toast.error('Falha ao carregar as Gestões de Mudança.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const kpis = useMemo(() => {
        const counts = gmuds.reduce((acc, g) => {
            acc[g.status] = (acc[g.status] || 0) + 1;
            return acc;
        }, {});
        
        return [
            { label: 'Total GMUDs',   value: gmuds.length, color: 'var(--gmud-text-primary)', icon: FaClipboardList },
            { label: 'Pendentes',     value: counts.Pendente || 0,      color: 'var(--gmud-status-pending)',  sub: 'Aguardando aprovação', icon: FaHourglassHalf },
            { label: 'Em Execução',   value: counts['Em Execução'] || 0, color: 'var(--gmud-status-running)', sub: 'Em andamento',         icon: FaPlayCircle },
            { label: 'Concluídas',    value: counts.Concluída || 0,     color: 'var(--gmud-status-done)',     sub: 'Finalizadas',          icon: FaRegCheckCircle },
            { label: 'Rejeitadas',    value: counts.Rejeitada || 0,     color: 'var(--gmud-status-rejected)', sub: 'Interrompidas',        icon: FaExclamationCircle },
        ];
    }, [gmuds]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return gmuds.filter(g => {
            const matchSearch = !q || g.titulo?.toLowerCase().includes(q) || String(g.id).includes(q) || g.solicitante_nome?.toLowerCase().includes(q);
            const matchStatus = !filterStatus || g.status === filterStatus;
            const matchTipo   = !filterTipo   || g.tipo === filterTipo;
            return matchSearch && matchStatus && matchTipo;
        });
    }, [gmuds, search, filterStatus, filterTipo]);

    const uniqueTipos = useMemo(() => [...new Set(gmuds.map(g => g.tipo).filter(Boolean))], [gmuds]);

    return (
        <div className="gmud-container">
            {/* ── Header ─────────────────────────────────────── */}
            <header className="gmud-header">
                <div className="gmud-header-content">
                    <div className="gmud-header-icon-wrapper">
                        <FaTools className="gmud-header-icon" />
                    </div>
                    <div>
                        <h1 className="gmud-header-title">Gestão de Mudanças</h1>
                        <p className="gmud-header-subtitle">Controle, governança e aprovação de GMUDs da plataforma</p>
                    </div>
                </div>
            </header>

            {/* ── KPIs ────────────────────────────────────────── */}
            <section className="gmud-kpi-container">
                {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
            </section>

            {/* ── Toolbar ─────────────────────────────────────── */}
            <section className="gmud-toolbar-container">
                <div className="gmud-toolbar-search">
                    <FaSearch size={14} className="gmud-search-icon" />
                    <input
                        type="text"
                        className="gmud-input gmud-input-search"
                        placeholder="Buscar por título, ID ou solicitante..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                
                <div className="gmud-toolbar-filters">
                    <div className="gmud-filter-group">
                        <FaFilter size={12} className="gmud-filter-icon" />
                        <select 
                            value={filterStatus} 
                            onChange={e => setFilterStatus(e.target.value)} 
                            className="gmud-input gmud-select"
                        >
                            <option value="">Todos os status</option>
                            {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    <div className="gmud-filter-group">
                        <select 
                            value={filterTipo} 
                            onChange={e => setFilterTipo(e.target.value)} 
                            className="gmud-input gmud-select"
                        >
                            <option value="">Todos os tipos</option>
                            {uniqueTipos.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    {(filterStatus || filterTipo || search) && (
                        <button className="gmud-btn gmud-btn-clear" onClick={() => { setSearch(''); setFilterStatus(''); setFilterTipo(''); }}>
                            Limpar filtros
                        </button>
                    )}
                </div>
            </section>

            <div className="gmud-results-info">
                <span className="gmud-results-text">Exibindo {filtered.length} registro{filtered.length !== 1 ? 's' : ''}</span>
            </div>

            {/* ── Table ───────────────────────────────────────── */}
            <section className="gmud-table-container">
                <div className="gmud-table-wrapper">
                    <table className="gmud-table">
                        <thead className="gmud-table-head">
                            <tr>
                                <th className="gmud-th col-id">ID</th>
                                <th className="gmud-th col-titulo">Título</th>
                                <th className="gmud-th col-status">Status</th>
                                <th className="gmud-th col-tipo">Tipo</th>
                                <th className="gmud-th col-date">Início Planejado</th>
                                <th className="gmud-th col-date">Fim Planejado</th>
                                <th className="gmud-th col-user">Solicitante</th>
                                <th className="gmud-th col-action"></th>
                            </tr>
                        </thead>
                        <tbody className="gmud-table-body">
                            {loading
                                ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                                : filtered.length > 0
                                    ? filtered.map(gmud => (
                                        <tr
                                            key={gmud.id}
                                            className="gmud-table-row"
                                            onClick={() => navigate(`/suporte-admin/gmud/${gmud.id}`)}
                                        >
                                            <td className="gmud-td col-id">
                                                <span className="gmud-id-tag">#{gmud.id}</span>
                                            </td>
                                            <td className="gmud-td col-titulo">
                                                <span className="gmud-text-titulo" title={gmud.titulo}>{gmud.titulo}</span>
                                            </td>
                                            <td className="gmud-td col-status">
                                                <StatusBadge status={gmud.status} />
                                            </td>
                                            <td className="gmud-td col-tipo">
                                                <TipoBadge tipo={gmud.tipo} />
                                            </td>
                                            <td className="gmud-td col-date">
                                                <div className="gmud-date-wrapper">
                                                    <FaCalendarAlt className="gmud-date-icon" />
                                                    <span>{formatDate(gmud.janela_inicio)}</span>
                                                </div>
                                            </td>
                                            <td className="gmud-td col-date">
                                                <div className="gmud-date-wrapper">
                                                    <FaCalendarAlt className="gmud-date-icon" />
                                                    <span>{formatDate(gmud.janela_fim)}</span>
                                                </div>
                                            </td>
                                            <td className="gmud-td col-user">
                                                <div className="gmud-user-wrapper">
                                                    <div className="gmud-user-avatar">
                                                        <FaUser size={10} />
                                                    </div>
                                                    <span>{gmud.solicitante_nome}</span>
                                                </div>
                                            </td>
                                            <td className="gmud-td col-action">
                                                <FaChevronRight className="gmud-action-arrow" />
                                            </td>
                                        </tr>
                                    ))
                                    : (
                                        <tr>
                                            <td colSpan={8} className="gmud-td gmud-td-empty">
                                                <div className="gmud-empty-state">
                                                    <div className="gmud-empty-icon-wrapper">
                                                        <FaLayerGroup size={36} />
                                                    </div>
                                                    <h3 className="gmud-empty-title">Nenhuma GMUD encontrada</h3>
                                                    <p className="gmud-empty-subtitle">Não encontramos registros para os filtros selecionados atualmente.</p>
                                                    {(search || filterStatus || filterTipo) && (
                                                        <button className="gmud-btn gmud-btn-outline" onClick={() => { setSearch(''); setFilterStatus(''); setFilterTipo(''); }}>
                                                            Limpar todos os filtros
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                            }
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default GmudDashboard;